import type { BomMaterialLine, BomToolLine, PriceTier } from '@/features/projects/rollup-bom'

/**
 * Phase 17 — one row in a {@link CartGroup}, unified across materials and tools so the cart
 * preview UI doesn't branch per kind. Materials carry a {@code unit}; tools have {@code unit:
 * null}. {@code quantity} is whatever the upstream rollup decided — for the editor it's the raw
 * BOM total, for the doing view it's already after Inventory + Acquisitions.
 */
export interface CartItem {
  kind: 'MATERIAL' | 'TOOL'
  catalogId: string
  name: string
  unit: string | null
  quantity: number
  /** Resolved unit price (Locked > Live > Listed, per the Phase 16 three-tier priority). */
  unitPriceCents: number
  priceTier: PriceTier
  isLivePriceStale: boolean
  /** Fallback chain: prefer affiliate URL when set, else the bare sourceUrl. */
  sourceUrl: string
  affiliateUrl: string | null
  /** {@code sourceUrl} is required to enter the cart, but {@code affiliateUrl} only when set. */
  sourceSlug: string
  sourceDisplayName: string
}

export interface CartGroup {
  sourceSlug: string
  sourceDisplayName: string
  items: CartItem[]
  /** Sum of {@code unitPriceCents × quantity} across items. Locked items don't reach here. */
  subtotalCents: number
  /** True when at least one item's Live price is past the staleness threshold. */
  hasStaleLivePrice: boolean
  /** True when at least one item has an {@code affiliateUrl} set (drives disclosure visibility). */
  hasAnyAffiliateLink: boolean
}

/**
 * Group BOM lines into per-source carts, applying the inclusion rules:
 *
 * <ol>
 *   <li>Skip lines with no {@code liveSource} — nothing to checkout for those.
 *   <li>Skip {@code locked} tier lines — already paid via Phase 12 acquisition.
 *   <li>Skip lines with quantity ≤ 0 — already covered by Inventory in the doing view (the editor
 *       pass always has positive quantity since it predates Inventory subtraction).
 * </ol>
 *
 * <p>Pure function — re-runs cheaply on every render via {@code useMemo}; no caching.
 */
export const groupCartItemsBySource = (
  bomLines: ReadonlyArray<BomMaterialLine | BomToolLine>
): CartGroup[] => {
  const bySlug = new Map<string, CartGroup>()

  for (const line of bomLines) {
    if (!line.liveSource) continue
    if (line.priceTier === 'locked') continue
    const quantity = isMaterialLine(line) ? line.neededQty : line.owned ? 0 : 1
    if (quantity <= 0) continue

    const slug = line.liveSource.sourceSlug
    let group = bySlug.get(slug)
    if (!group) {
      group = {
        sourceSlug: slug,
        sourceDisplayName: line.liveSource.sourceDisplayName,
        items: [],
        subtotalCents: 0,
        hasStaleLivePrice: false,
        hasAnyAffiliateLink: false,
      }
      bySlug.set(slug, group)
    }

    const item: CartItem = {
      kind: isMaterialLine(line) ? 'MATERIAL' : 'TOOL',
      catalogId: isMaterialLine(line) ? line.catalogMaterialId : line.catalogToolId,
      name: line.name,
      unit: isMaterialLine(line) ? line.unit : null,
      quantity,
      unitPriceCents: line.unitPriceCents,
      priceTier: line.priceTier,
      isLivePriceStale: line.isLivePriceStale,
      sourceUrl: line.liveSource.sourceUrl,
      affiliateUrl: line.liveSource.affiliateUrl,
      sourceSlug: line.liveSource.sourceSlug,
      sourceDisplayName: line.liveSource.sourceDisplayName,
    }

    group.items.push(item)
    group.subtotalCents += Math.round(item.unitPriceCents * item.quantity)
    if (item.isLivePriceStale) group.hasStaleLivePrice = true
    if (item.affiliateUrl) group.hasAnyAffiliateLink = true
  }

  for (const group of bySlug.values()) {
    group.items.sort((a, b) => a.name.localeCompare(b.name))
  }

  return [...bySlug.values()].sort((a, b) => a.sourceDisplayName.localeCompare(b.sourceDisplayName))
}

const isMaterialLine = (line: BomMaterialLine | BomToolLine): line is BomMaterialLine =>
  'catalogMaterialId' in line
